import {useState, useEffect, useRef, useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {usePreview} from '../contexts/StorefrontPreviewContext'

const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

// Module-level in-memory cache shared across all hook instances
const _responseCache = new Map()

// In-flight promise map — prevents duplicate concurrent fetches for the same key
const _pendingFetches = new Map()

// ---------------------------------------------------------------------------
// Prefetch queue — limits background prefetches to MAX_CONCURRENT so they
// don't saturate the browser connection pool and starve page-content fetches
// (hero, footer, carousels, etc.) which always bypass this queue.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT_PREFETCHES = 2
let _prefetchActive = 0
const _prefetchQueue = []

const _processPrefetchQueue = () => {
    while (_prefetchActive < MAX_CONCURRENT_PREFETCHES && _prefetchQueue.length > 0) {
        const {slotParam, previewData, resolve, reject} = _prefetchQueue.shift()
        _prefetchActive++
        _fetchAndCache(slotParam, previewData, undefined)
            .then(resolve)
            .catch(reject)
            .finally(() => {
                _prefetchActive--
                _processPrefetchQueue()
            })
    }
}

const _queuePrefetch = (slotParam, previewData) =>
    new Promise((resolve, reject) => {
        _prefetchQueue.push({slotParam, previewData, resolve, reject})
        _processPrefetchQueue()
    })

// ---------------------------------------------------------------------------
// appConfig is static — resolving it once at module level removes it from
// useEffect dependency arrays, which was causing effects to re-run on every
// render (each getConfig() call returns a new object reference).
// ---------------------------------------------------------------------------
let _appConfig = null
const _getAppConfig = () => {
    if (!_appConfig) {
        try {
            _appConfig = getConfig().app
        } catch {
            // getConfig may throw during SSR before config is ready
        }
    }
    return _appConfig
}

const _getFromCache = (key) => {
    const entry = _responseCache.get(key)
    if (!entry) return null
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        _responseCache.delete(key)
        return null
    }
    return entry.value
}

const _setCache = (key, value) => {
    _responseCache.set(key, {value, ts: Date.now()})
}

const _buildFetchArgs = (slotParam, previewData) => {
    const appConfig = _getAppConfig()
    let fetchOptions = {}
    if (appConfig?.storefrontApi?.userName && appConfig?.storefrontApi?.password) {
        fetchOptions = {
            headers: {
                Authorization:
                    'Basic ' +
                    btoa(`${appConfig.storefrontApi.userName}:${appConfig.storefrontApi.password}`)
            }
        }
    }

    let host = ''
    let previewQueryString = ''
    let isPreview = false
    if (typeof window !== 'undefined' && window.location.href?.includes('mobify-storefront.com')) {
        host = appConfig?.commerceAPI?.proxyPathOcapi || ''
        previewQueryString = Object.keys(previewData).length
            ? '&' + new URLSearchParams(previewData).toString()
            : ''
        isPreview = true
    }

    return {
        url: `${host}/getcustomcontent/?${slotParam}${previewQueryString}`,
        fetchOptions,
        isPreview
    }
}

/**
 * Shared fetch with in-flight deduplication.
 * Concurrent callers for the same key share a single in-flight promise.
 * Page-content fetches (via useCustomFetch) call this directly.
 * Background prefetches call this through _queuePrefetch.
 */
const _fetchAndCache = (key, previewData, signal) => {
    if (_pendingFetches.has(key)) {
        return _pendingFetches.get(key)
    }

    const promise = (async () => {
        const {url, fetchOptions, isPreview} = _buildFetchArgs(key, previewData)
        const res = await fetch(url, {...fetchOptions, signal})
        if (!res.ok) throw new Error('Failed to fetch content')
        let html = await res.text()
        if (isPreview) {
            html = html.replace(/href=(["'])https:\/\/(dev|staging)\.cavenders\.com/g, 'href=$1')
        }
        _setCache(key, html)
        return html
    })().finally(() => {
        _pendingFetches.delete(key)
    })

    _pendingFetches.set(key, promise)
    return promise
}

/**
 * Standalone prefetch — warms the cache for a slot before the user hovers.
 * Skips if already cached or in-flight. Goes through the throttled queue so
 * background prefetches do not compete with page-content fetches.
 */
export const prefetchSlotContent = async (slotParam, _ignoredAppConfig, previewData = {}) => {
    if (_getFromCache(slotParam) !== null) return
    if (_pendingFetches.has(slotParam)) return

    try {
        await _queuePrefetch(slotParam, previewData)
    } catch (err) {
        console.error('Error prefetching slot content:', err)
    }
}

const useCustomFetch = (token, ids, isSlot = true) => {
    const cacheKey = ids ? (isSlot ? `c_contentSlotIDs=${ids}` : `c_contentAssetIDs=${ids}`) : null
    const initialCached = cacheKey ? _getFromCache(cacheKey) : null

    const slotParams = useMemo(
        () => (ids ? (isSlot ? `c_contentSlotIDs=${ids}` : `c_contentAssetIDs=${ids}`) : null),
        [ids, isSlot]
    )

    const [response, setResponse] = useState(() => initialCached)
    const [loading, setLoading] = useState(() => !ids || initialCached === null)
    const [error, setError] = useState(null)
    const {previewData = {}} = usePreview()
    // Stable ref so the effect doesn't need previewData in its dep array
    // (which would cause refetches on every preview context tick)
    const previewDataRef = useRef(previewData)
    previewDataRef.current = previewData

    useEffect(() => {
        if (!slotParams) return

        const cached = _getFromCache(slotParams)
        if (cached !== null) {
            setResponse(cached)
            setLoading(false)
            return
        }

        const controller = new AbortController()

        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                // Page-content fetches bypass the prefetch queue — they are
                // immediate and take priority over background prefetches.
                const html = await _fetchAndCache(
                    slotParams,
                    previewDataRef.current,
                    controller.signal
                )
                setResponse(html)
            } catch (err) {
                if (err.name === 'AbortError') return
                console.error('Error in fetching custom API:', err)
                setError(err.message || 'An unexpected error occurred')
            } finally {
                setLoading(false)
            }
        }

        fetchData()

        return () => {
            controller.abort()
        }
    }, [slotParams])
    // appConfig intentionally omitted from deps — it is static config resolved
    // once at module level. Including it caused a new object reference on every
    // render, re-triggering the effect and spawning duplicate concurrent fetches.

    return {response, loading, error}
}

export default useCustomFetch
